/**
 * Pacific gas-day range helpers.
 *
 * Data-visualization works in inclusive Pacific gas-days (`YYYY-MM-DD`) — the
 * same day convention the batch boundary uses (`12:01 AM PT day 1 → 12:00 AM
 * PT next period`, per `time.ts`). These helpers operate purely on the
 * calendar-date string so they never drift across a DST transition the way a
 * `Date` + millisecond arithmetic can. String comparison is correct ordering
 * for `YYYY-MM-DD`.
 */
import { formatPacific } from "@compliance/domain";

/** Hard cap mirrored from the backend `/data/series` contract (≤ 370 days). */
export const MAX_RANGE_DAYS = 370;

/** Today as a PT gas-day string (`YYYY-MM-DD`). */
export function todayPacific(): string {
  return formatPacific(new Date()).slice(0, 10);
}

/** Canonical PT gas-day form (`YYYY-MM-DD`). */
const GAS_DAY_RE = /^\d{4}-\d{2}-\d{2}$/;

/** Shift a `YYYY-MM-DD` day by `delta` days (UTC math on the date components). */
export function addDaysIso(day: string, delta: number): string {
  if (!GAS_DAY_RE.test(day)) {
    // Fail loud rather than silently masking bad input to a 1970-based window
    // (or, on a NaN component, an Invalid-time throw deeper in). Every real
    // caller passes a canonical gas-day, so a non-canonical value here is a
    // contract violation worth surfacing, not hiding (BIO-144 #5).
    throw new RangeError(`addDaysIso: expected a YYYY-MM-DD gas-day, got "${day}"`);
  }
  const [y, m, d] = day.split("-").map(Number);
  const t = Date.UTC(y, m - 1, d) + delta * 86_400_000;
  return new Date(t).toISOString().slice(0, 10);
}

/**
 * Inclusive list of gas-days from `start` to `end`. Returns `[]` for an
 * inverted range and is bounded at `MAX_RANGE_DAYS + 1` so a pathological
 * input can never spin.
 *
 * Non-canonical bounds (a cleared date field, a hand-edited URL param) return
 * `[]` rather than letting the now-strict `addDaysIso` throw inside the loop —
 * the app has no error boundary, so a throw here would white-screen the route
 * instead of showing an empty chart (BIO-144 #5 follow-up).
 */
export function enumerateDays(start: string, end: string): string[] {
  if (!GAS_DAY_RE.test(start) || !GAS_DAY_RE.test(end)) return [];
  if (start > end) return [];
  const out: string[] = [];
  let cur = start;
  while (cur <= end && out.length <= MAX_RANGE_DAYS + 1) {
    out.push(cur);
    cur = addDaysIso(cur, 1);
  }
  return out;
}

/** Inclusive day count for the range (0 when inverted). */
export function dayCount(start: string, end: string): number {
  return enumerateDays(start, end).length;
}

/** First day of the calendar month containing `day`. */
export function startOfMonth(day: string): string {
  return `${day.slice(0, 7)}-01`;
}

/** Convert a tz-aware ISO instant (batch boundary) to its PT gas-day. */
export function isoToGasDay(iso: string): string {
  // The batch period_start already carries the PT offset, so the leading
  // date is the gas-day. We avoid re-zoning to keep this dependency-light.
  return iso.slice(0, 10);
}
